// features/dashboardsState.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RootState, Dashboard } from 'types/types';
import { current } from 'immer';
import LocalForageService from 'services/LocalForageService';

export interface DashboardsState {
  dashboards: Dashboard[];
  visibleDashboardId: string;
  registeringPods: string[];
}

const initialState: DashboardsState = {
  dashboards: [],
  visibleDashboardId: '',
  registeringPods: [],
};

const dashboardsSlice = createSlice({
  name: 'dashboardsState',
  initialState,
  reducers: {
    setDashboardDescriptor: (
      state,
      action: PayloadAction<{ dashboardId: string; descriptor: any }>
    ) => {
      const { dashboardId, descriptor } = action.payload;
      const dashboard = state.dashboards.find(
        (_) => _.dashboardId === dashboardId
      );
      if (dashboard) {
        dashboard.descriptor = descriptor;
        state.visibleDashboardId = dashboardId;
      } else {
        const newDescriptor = Object.assign({}, descriptor);
        newDescriptor.filters = newDescriptor.filters.map((filter) => {
          filter = Object.assign({}, filter);
          filter.dashboardId = dashboardId;

          if (filter.linkedPodIds) {
            filter.linkedPodIds.forEach((podId) => {
              const pod = newDescriptor.pods.find((_) => _.podId === podId);
              if (pod) {
                if (pod.related_filters &&
                  !pod.related_filters.includes(filter.filterId)) {
                  pod.related_filters.push(filter.filterId);
                } else {
                  pod.related_filters = [filter.filterId];
                  if (pod.filters) {
                    pod.filters.forEach((_) => {
                      pod.related_filters.push(_.filterId);
                    });
                  }
                }
              }
            });
          }

          return filter;
        });

        state.dashboards.push({
          dashboardId,
          descriptor: newDescriptor,
          isVisible: true,
          filters: {
            refreshPodIds: [],
            maximizePodId: '',
            filters: [],
            forceRefresh: false,
          },
        });
        state.visibleDashboardId = dashboardId;
      }
      const lsService = new LocalForageService();
      lsService.setItem('dashboards', current(state).dashboards);
    },
    setDashboardVisibility: (
      state,
      action: PayloadAction<{ dashboardId: string }>
    ) => {
      const { dashboardId } = action.payload;
      state.visibleDashboardId = dashboardId;
    },
    removeDashboard: (state, action: PayloadAction<string>) => {
      const dashboardIdToRemove = action.payload;
      state.dashboards = state.dashboards.filter(
        (dashboard) => dashboard.dashboardId !== dashboardIdToRemove
      );
      const lsService = new LocalForageService();
      lsService.setItem('dashboards', current(state).dashboards);
    },
    registeringPod: (state, action: PayloadAction<string>) => {
      state.registeringPods.push(action.payload);
    },
    registeringPodDone: (state, action: PayloadAction<string>) => {
      state.registeringPods = state.registeringPods.filter(
        (id) => id !== action.payload
      );
    },
  },
});

export const {
  setDashboardDescriptor,
  setDashboardVisibility,
  removeDashboard,
  registeringPod,
  registeringPodDone,
} = dashboardsSlice.actions;

export const dashboards = (state: { dashboards: DashboardsState }) => {
  return state.dashboards.dashboards; // Changed from state.dashboardsState.dashboards to state.dashboards.dashboards
};

export const visibleDashboardId = (state: { dashboards: DashboardsState }) => {
  return state.dashboards.visibleDashboardId; // Changed from state.dashboardsState.dashboards to state.dashboards.dashboards
};

export const registeringPods = (state: { dashboards: DashboardsState }) => {
  return state.dashboards.registeringPods; // Changed from state.dashboardsState.dashboards to state.dashboards.dashboards
};

export const selectDashboardById = (state: RootState, dashboardId: string) => {
  return state.dashboards.dashboards.find((d) => d.dashboardId === dashboardId);
};

export const selectMaximizePodId = createSelector(
  [selectDashboardById],
  (dashboard) => dashboard?.filters.maximizePodId
);

export const selectFilters = createSelector(
  [selectDashboardById],
  (dashboard) => dashboard?.filters.filters
);

export const selectRefreshPodIds = createSelector(
  [selectDashboardById],
  (dashboard) => dashboard?.filters.refreshPodIds
);

export const selectRefresh = createSelector(
  [selectDashboardById],
  (dashboard) => dashboard?.filters.forceRefresh
);

export default dashboardsSlice.reducer;
